import React, { useMemo, useState } from "react";
import { renderTextWithLineBreaks } from "../../utils/DeleteBr";
import { useUser } from "../../context/UserContext";
import { AlertTriangle, ChevronLeft } from 'lucide-react';
import PastilleNiveau from "../CollerNotes/PastilleNiveau";

const NotificationOption = ({ label, onChange, checked }) => (
  <div
    key={label}
    onClick={onChange}
    className={`
      flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 xl:p-1.5 2xl:p-2 3xl:p-2
      rounded-lg sm:rounded-xl cursor-pointer transition-all
      text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm duration-300 border-2
      ${checked
        ? 'bg-green-50 border-green-400 text-green-700 shadow-md'
        : 'bg-gray-100 border-transparent hover:bg-indigo-50 hover:border-indigo-400'
      }
    `}
  >
    {/* Checkbox personnalisée */}
    <div className={`
      w-4 h-4 sm:w-5 sm:h-5 border-2 rounded flex items-center justify-center transition-all duration-300
      ${checked
        ? 'bg-green-500 border-green-500 text-white'
        : 'border-gray-300 bg-white'
      }
    `}>
      {checked && (
        <span className="text-xs font-bold">✓</span>
      )}
    </div>
    
    {/* Label de la compétence - responsive */}
    <span className={`
      font-medium transition-colors duration-300 text-xs sm:text-sm
      ${checked 
        ? 'text-green-700 font-semibold' 
        : 'text-gray-600'
      }
    `}>
      {/* Version courte sur mobile pour certains labels longs */}
      <span className="sm:hidden">
        {label.length > 25 ? label.substring(0, 20) + '...' : label}
      </span>
      <span className="hidden sm:inline">
        {label}
      </span>
    </span>
  </div>
);

export default function InfoCompetences({
  handlePrev,
  selectedSavoirs,
  onChange,
  selectedComps,
  selectedAttendus,
  selectedTheme,
  selectedDescripteur,
  isLoadingPost,
  handleSubmit,
  niveauClasse = ''
}) {
  const { user } = useUser();
  const compsMath = ["Calculer", "Chercher", "Raisonner", "Modéliser", "Communiquer", "Représenter"];
  const compsFra = [
    "Comprendre et s'exprimer à l'oral",
    "Lire",
    "Écrire",
    "Comprendre le fonctionnement de la langue",
    "Acquérir des éléments de culture littéraire et artistique",
  ];

  // Fonctions existantes SANS CHANGEMENT
  const getTotalSelected = () => {
    if (!selectedComps || !Array.isArray(selectedComps)) return 0;
    
    return selectedComps.reduce((total, item) => {
      return total + (item.competences ? item.competences.length : 0);
    }, 0);
  };

  const getTotalCards = () => {
    const savoirsCount = selectedSavoirs ? selectedSavoirs.length : 0;
    const attendusCount = selectedAttendus ? selectedAttendus.length : 0;
    return savoirsCount + attendusCount;
  };

  const [comps] = useState(user.matiere === "maths" ? compsMath : compsFra);

  // Cartes (savoir-faire / attendu) auxquelles aucune compétence n'est encore
  // associée : tant qu'il en reste, on bloque la finalisation et on l'explique
  // (avant, le bouton Finaliser disparaissait simplement, sans message).
  // selectedComps ne contient qu'une entrée par carte ayant ≥ 1 compétence
  // (cf. SET_COMPETENCES / purge au décochage dans addEvalReducer), donc une
  // carte absente de cet ensemble = carte sans compétence.
  const cartesSansComp = useMemo(() => {
    const avecComp = new Set((selectedComps || []).map((o) => `${o.type}-${o.savoir.id}`));
    const manquantes = [];
    selectedSavoirs.forEach((s) => {
      if (!avecComp.has(`savoir-${s.id}`)) manquantes.push({ key: `savoir-${s.id}`, label: s.nom });
    });
    selectedAttendus.forEach((a) => {
      if (!avecComp.has(`attendu-${a.id}`)) manquantes.push({ key: `attendu-${a.id}`, label: a.description });
    });
    return manquantes;
  }, [selectedComps, selectedSavoirs, selectedAttendus]);

  return (
    <div className="w-full max-w-6xl mx-auto bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-white/20">
      {/* Header responsive */}
      <div className="bg-gradient-to-r from-blue-500 to-sky-600 text-white text-center py-4 sm:py-6 lg:py-8 xl:py-4 2xl:py-5 3xl:py-8 px-4 sm:px-6 lg:px-10 xl:px-5 2xl:px-7 3xl:px-10 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full"></div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-lg 2xl:text-xl 3xl:text-3xl font-bold mb-1 sm:mb-2 relative z-10">
          <span className="hidden sm:inline">Configuration des Compétences</span>
          <span className="sm:hidden">Compétences</span>
        </h1>
        <p className="text-xs sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg opacity-90 relative z-10 px-2">
          <span className="hidden sm:inline">Sélectionnez les compétences spécifiques pour chaque savoir-faire</span>
          <span className="sm:hidden">Sélectionnez pour chaque savoir</span>
        </p>
      </div>

      <div className="p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 bg-gray-50 h-fit overflow-auto">
        {/* Zone scrollable avec les cartes de compétences - responsive */}
        <div className="  pr-1 sm:pr-2 space-y-3 sm:space-y-4 lg:space-y-6">
          {/* Savoirs */}
          {selectedSavoirs.map((savoir, index) => {
            const chosen = selectedComps.find((o) => o.type === "savoir" && o.savoir.id === savoir.id)?.competences || [];
            return (
              <div key={savoir.id} 
                className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl
                p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200 relative overflow-hidden 
                transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: 'fadeInUp 0.6s ease-out'
                }}
              >
                {/* Barre latérale colorée */}
                <div className="absolute left-0 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-teal-500 to-green-600"></div>
                
                <div className="pl-2 sm:pl-3 lg:pl-4 mb-3 sm:mb-4 lg:mb-5">
                  <h3 className="text-xs sm:text-sm xl:text-base font-semibold text-gray-700 line-clamp-3">
                    {renderTextWithLineBreaks(savoir.nom)}
                  </h3>
                </div>
                
                {/* Grille des options de compétences - responsive */}
                <div className="pl-2 sm:pl-3 lg:pl-4 grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                  {comps.map((comp) => (
                    <NotificationOption
                      key={comp}
                      label={comp}
                      onChange={() => onChange(savoir, comp, 'savoir')}
                      checked={chosen.includes(comp)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Attendus */}
          {selectedAttendus.map((attendu, index) => {
            const chosen = selectedComps.find((o) => o.type === "attendu" && o.savoir.id === attendu.id)?.competences || [];
            return (
              <div key={attendu.id} 
                className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl 
                p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 shadow-lg border border-gray-200 relative overflow-hidden 
                transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: 'fadeInUp 0.6s ease-out'
                }}
              >
                {/* Barre latérale colorée */}
                <div className="absolute left-0 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-sky-500 to-blue-600"></div>
                
                <div className="pl-2 sm:pl-3 lg:pl-4 mb-3 sm:mb-4 lg:mb-5 flex items-baseline gap-2">
                  {/* « 5e » DEVANT le texte : attendu pris dans un autre niveau que la
                      classe (au bout de la ligne, sur une carte pleine largeur, on ne
                      la voyait pas). */}
                  <PastilleNiveau attendu={attendu} niveauClasse={niveauClasse} />
                  <h3 className="flex-1 min-w-0 text-xs sm:text-sm xl:text-base font-semibold text-gray-800 leading-relaxed line-clamp-3">
                    {renderTextWithLineBreaks(attendu.description)}
                  </h3>
                </div>
                
                {/* Grille des options de compétences - responsive */}
                <div className="pl-2 sm:pl-3 lg:pl-4 grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                  {comps.map((comp) => (
                    <NotificationOption
                      key={comp}
                      label={comp}
                      onChange={() => onChange(attendu, comp, 'attendu')}
                      checked={chosen.includes(comp)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Résumé des sélections - responsive */}
        <div className="mt-4 sm:mt-6 lg:mt-8 xl:mt-4 2xl:mt-5 3xl:mt-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-3 sm:p-4 lg:p-6 xl:p-3 2xl:p-4 3xl:p-6 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-20 sm:w-24 h-20 sm:h-24 bg-white/10 rounded-full"></div>
          
          <div className="flex items-center gap-2 sm:gap-3 mb-2 relative z-10">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center">
              <span className="text-base sm:text-xl">📊</span>
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg font-bold">
              <span className="hidden sm:inline">Résumé de vos sélections</span>
              <span className="sm:hidden">Résumé</span>
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 relative z-10">
            <div className="bg-white/10 p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl text-center backdrop-blur-sm">
              <div className="text-lg sm:text-xl lg:text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold mb-0.5 sm:mb-1">{getTotalSelected()}</div>
              <div className="text-xs sm:text-sm opacity-90">
                <span className="hidden sm:inline">Compétences sélectionnées</span>
                <span className="sm:hidden">Compétences</span>
              </div>
            </div>
            
            <div className="bg-white/10 p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl text-center backdrop-blur-sm">
              <div className="text-lg sm:text-xl lg:text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold mb-0.5 sm:mb-1">{getTotalCards()}</div>
              <div className="text-xs sm:text-sm opacity-90">
                <span className="hidden sm:inline">Savoirs-faires configurés</span>
                <span className="sm:hidden">Savoirs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Boutons d'action - responsive */}
        <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-5 items-stretch sm:items-center pt-4 sm:pt-6 lg:pt-8 xl:pt-4 2xl:pt-5 3xl:pt-8 mt-4 sm:mt-6 lg:mt-8 xl:mt-4 2xl:mt-5 3xl:mt-8 border-t border-gray-200">
          <button
            onClick={handlePrev}
            className="flex items-center justify-center gap-2 sm:gap-3 
            px-4 sm:px-6 lg:px-9 xl:px-4 2xl:px-6 3xl:px-9 py-2.5 sm:py-3 lg:py-4 xl:py-2 2xl:py-2.5 3xl:py-4
            bg-gradient-to-r from-orange-500 to-orange-600
            text-xs sm:text-sm xl:text-xs 2xl:text-sm 3xl:text-lg text-white font-semibold 
            rounded-lg sm:rounded-xl lg:rounded-2xl 
            transition-all duration-300 hover:scale-[1.02] sm:hover:scale-105 hover:shadow-xl
            w-full sm:w-auto"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Retour aux savoirs-faires</span>
            <span className="sm:hidden">Retour</span>
          </button>
          
          {!isLoadingPost && (
            cartesSansComp.length === 0 ? (
              <button
                onClick={handleSubmit}
                className="flex items-center justify-center gap-2
                px-4 sm:px-6 lg:px-8 xl:px-4 2xl:px-5 3xl:px-8 py-2.5 sm:py-3 lg:py-4 xl:py-2 2xl:py-2.5 3xl:py-4
                bg-gradient-to-r from-green-500 to-emerald-600
                text-white font-semibold rounded-lg sm:rounded-xl
                text-xs sm:text-sm lg:text-lg xl:text-xs 2xl:text-sm 3xl:text-lg
                transition-all duration-300 hover:scale-[1.02] sm:hover:scale-105 hover:shadow-xl
                uppercase tracking-wide w-full sm:w-auto"
              >
                <span>✅</span>
                <span className="hidden sm:inline">Finaliser la configuration</span>
                <span className="sm:hidden">Finaliser</span>
              </button>
            ) : (
              <div
                role="status"
                className="w-full sm:w-auto sm:max-w-md flex items-start gap-2.5 px-4 py-3 rounded-lg sm:rounded-xl bg-amber-50 border-2 border-amber-300"
              >
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500" />
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-amber-800">
                    {cartesSansComp.length === 1
                      ? "1 savoir-faire sans compétence"
                      : `${cartesSansComp.length} savoirs-faires sans compétence`}
                  </p>
                  <p className="text-[11px] sm:text-xs text-amber-700">
                    Cochez au moins une compétence par carte pour pouvoir finaliser.
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {cartesSansComp.map((c) => {
                      const texte = c.label.replace(/<br>/g, ' ');
                      return (
                        <span
                          key={c.key}
                          title={texte}
                          className="inline-block max-w-[11rem] truncate bg-amber-100 border border-amber-300 text-amber-800 rounded-md px-2 py-0.5 text-[11px] font-medium"
                        >
                          {texte}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}